'use client';

import { createContext, useContext, useCallback, useMemo, ReactNode, useState } from 'react';

/**
 * Interface defining the structure of module tracking attributes
 * @typedef {Object} AnalyticsModuleTrackingAttributes
 */
export interface AnalyticsModuleTrackingAttributes {
  /** Sent as `module_title`: Contentful `sectionName`, or the standalone module identifier. */
  moduleTitle: string;
  /** Sent as `module_name`: visible Contentful headline text, or empty for standalone modules. */
  moduleName: string;
  /** The index/position of the module on the page */
  moduleIndex: string;
  /** The URL where the module is located */
  moduleLocation: string;
  /** The type of module (e.g., 'hero', 'faq', 'cta') */
  moduleType: string;
  /** The title of the page containing the module */
  pageTitle: string;
  pageTags?: Array<{ id: string }>;
}

interface AnalyticsModuleTrackingContextType {
  attributes: AnalyticsModuleTrackingAttributes;
  updateAttributes: (newAttributes: Partial<AnalyticsModuleTrackingAttributes>) => void;
}

const AnalyticsModuleTrackingContext = createContext<AnalyticsModuleTrackingContextType | null>(null);

export interface AnalyticsModuleTrackingProviderProps {
  children: ReactNode;
  initialAttributes?: Partial<AnalyticsModuleTrackingAttributes>;
}

/**
 * Context provider for module-level analytics tracking in the application.
 * Provides a centralized way to manage and update tracking attributes for UI modules.
 *
 * @example
 * // Basic usage at the top level of your application
 * function App() {
 *   return (
 *     <AnalyticsModuleTrackingProvider>
 *       <YourAppComponents />
 *     </AnalyticsModuleTrackingProvider>
 *   );
 * }
 *
 * @example
 * // Usage with initial attributes
 * function HomePage() {
 *   const initialAttributes = {
 *     moduleType: 'homepage',
 *     pageTitle: 'Welcome to Chime',
 *   };
 *
 *   return (
 *     <AnalyticsModuleTrackingProvider initialAttributes={initialAttributes}>
 *       <HomePageContent />
 *     </AnalyticsModuleTrackingProvider>
 *   );
 * }
 *
 * @example
 * // Using the tracking hooks in a component
 * function HeroModule() {
 *   const { updateAttributes } = useAnalyticsModuleTracking();
 *
 *   useEffect(() => {
 *     updateAttributes({
 *       moduleTitle: 'Hero Banner',
 *       moduleIndex: '1',
 *       moduleType: 'hero'
 *     });
 *   }, []);
 *
 *   // Get formatted tracking attributes
 *   const trackingAttributes = useAnalyticsModuleTrackingAttributes();
 *
 *   return <div>Hero Content</div>;
 * }
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped
 * @param {Partial<AnalyticsModuleTrackingAttributes>} [props.initialAttributes] - Initial tracking attributes
 * @returns {JSX.Element} Provider component that enables module tracking
 */
export function AnalyticsModuleTrackingProvider({
  children,
  initialAttributes = {},
}: AnalyticsModuleTrackingProviderProps) {
  const [attributes, setAttributes] = useState<AnalyticsModuleTrackingAttributes>({
    moduleTitle: '',
    moduleName: '',
    moduleIndex: '1',
    moduleLocation: typeof window !== 'undefined' ? window.location.href : '',
    moduleType: '',
    pageTitle: '',
    ...initialAttributes,
  });

  const updateAttributes = useCallback((newAttributes: Partial<AnalyticsModuleTrackingAttributes>) => {
    setAttributes((prev: AnalyticsModuleTrackingAttributes) => ({
      ...prev,
      ...newAttributes,
    }));
  }, []);

  const value = useMemo(
    () => ({
      attributes,
      updateAttributes,
    }),
    [attributes, updateAttributes],
  );

  return <AnalyticsModuleTrackingContext.Provider value={value}>{children}</AnalyticsModuleTrackingContext.Provider>;
}

/**
 * Hook to access and update module tracking context.
 * Must be used within a ModuleTrackingProvider component.
 *
 * @example
 * function MyComponent() {
 *   const { attributes, updateAttributes } = useAnalyticsModuleTracking();
 *
 *   // Access current tracking attributes
 *   console.log(attributes.moduleTitle);
 *
 *   // Update tracking attributes
 *   updateAttributes({
 *     moduleTitle: 'New Title',
 *     moduleType: 'custom'
 *   });
 *
 *   return <div>My Component</div>;
 * }
 *
 * @returns {AnalyticsModuleTrackingContextType} Object containing tracking attributes and update function
 * @throws {Error} If used outside of a ModuleTrackingProvider
 */
export function useAnalyticsModuleTracking() {
  const context = useContext(AnalyticsModuleTrackingContext);

  if (!context) {
    throw new Error('useAnalyticsModuleTracking must be used within an AnalyticsModuleTrackingProvider');
  }

  return context;
}

/**
 * Hook that converts module tracking attributes into an array format suitable for analytics tracking.
 * Must be used within a ModuleTrackingProvider component.
 *
 * @example
 * function TrackableComponent() {
 *   const trackingAttributes = useAnalyticsModuleTrackingAttributes();
 *
 *   // Use with analytics tracking
 *   const handleClick = () => {
 *     trackEvent('button_click', trackingAttributes);
 *   };
 *
 *   return <button onClick={handleClick}>Track Me</button>;
 * }
 *
 * @returns {Array<{key: string, value: string}>} Array of tracking attributes in key-value format
 */
export function useAnalyticsModuleTrackingAttributes(): Array<{ key: string; value: string }> {
  const { attributes } = useAnalyticsModuleTracking();

  return useMemo(
    () => [
      { key: 'module_title', value: attributes.moduleTitle },
      { key: 'module_name', value: attributes.moduleName },
      { key: 'module_index', value: attributes.moduleIndex },
      { key: 'module_location', value: typeof window !== 'undefined' ? window.location.href : '' },
      { key: 'module_type', value: attributes.moduleType },
      { key: 'page_title', value: attributes.pageTitle },
    ],
    [attributes],
  );
}
